import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  ModelBase, 
} from "@models";
import { 
  BastionAbility,
  BastionFacility,
  CharacterLimitation, 
} from "@dndModels";
import { BastionFacilitySize } from "@dndModels/Types";
import { 
  BASTION_FACILITY_SIZE_OPTIONS, 
  BASTION_FACILITY_SIZES, 
  CHARACTER_LEVEL_NUMS 
} from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import CheckBox from "@components/input/CheckBox";
import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import ModelBaseInput from "@dndComponents/ModelBaseInput";
import BastionAbilityListInput from "@dndComponents/bastion/BastionAbilityListInput";
import BastionAbilityInput from "@dndComponents/bastion/BastionAbilityInput";
import AddButton from "@components/input/buttons/AddButton";
import CharacterLimitationBox from "@dndComponents/limitations/CharacterLimitationBox";
import SelectStringToHashBox from "@components/input/selects/SelectStringToHashBox";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function BastionFacilityEdit({ obj: param_obj }: Props) {
  const [obj, setObj] = useState<BastionFacility>(param_obj instanceof BastionFacility ? param_obj : api.applyWorkingOn(new BastionFacility()));
  const [change_time, setChangeTime] = useState(0);
  const [expanded_ability_id, setExpandedAbilityID] = useState("");
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        '&.tableHeader': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.secondary.dark,
        },
        '&.alternate': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.dark,
        },
      },
    });
  });
  
  const { classes, cx } = useStyles();

  const renderAbilities = () => {
    if (expanded_ability_id !== "") {
      const ability = obj.abilities.find(o => o.true_id === expanded_ability_id);
      if (ability) {
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <BastionAbilityInput 
              bf={obj} 
              ability={ability} 
              onChange={() => {
                const new_obj = obj;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }} 
              onDelete={() => {
                const new_obj = obj;
                new_obj.abilities = obj.abilities.filter(o => o.true_id !== ability.true_id);
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }} 
              onDone={() => {
                setExpandedAbilityID("");
              }} 
            />
          </Grid>
        );
      }
    }
    return (
      <Grid
        size={{
          xs: 12
        }}>
        <BastionAbilityListInput 
          abilities={obj.abilities} 
          onExpand={(ability: BastionAbility) => {
            setExpandedAbilityID(ability.true_id);
          }}
          onAdd={() => {
            const new_obj = obj;
            const new_ability = new BastionAbility();
            new_obj.abilities.push(new_ability);
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
            setExpandedAbilityID(new_ability.true_id);
          }}
        />
      </Grid>
    );
  }

  const renderSubtypes = () => {
    return (
      <>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header padded">
            Subtypes
          </span>
          { !disabled &&
            <AddButton
              disabled={disabled}
              onClick={ () => {
                const new_obj = obj;
                new_obj.subtypes.push("");
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        { obj.subtypes.map((subtype, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              <StringBox
                name="Name"
                value={subtype}
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  new_obj.subtypes[key] = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          );
        })}
      </>
    );
  }

  const renderCapacityForSize = () => {
    return (
      <>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header padded">
            Capacity for Size
          </span>
          { !disabled &&
            <AddButton
              disabled={disabled}
              onClick={ () => {
                const new_obj = obj;
                const keys = Object.keys(new_obj.capacity_for_size);
                switch (new_obj.size) {
                  case "Cramped":
                    if (keys.length < 3) {
                      if (!keys.includes("Cramped")) {
                        new_obj.capacity_for_size["Cramped"] = 1;
                      } else if (!keys.includes("Roomy")) {
                        new_obj.capacity_for_size["Roomy"] = 1;
                      } else {
                        new_obj.capacity_for_size["Vast"] = 1;
                      }
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  break;
                  case "Roomy": 
                    if (keys.length < 2) {
                      if (!keys.includes("Roomy")) {
                        new_obj.capacity_for_size["Roomy"] = 1;
                      } else {
                        new_obj.capacity_for_size["Vast"] = 1;
                      }
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  break;
                  case "Vast":
                    if (keys.length === 0) {
                      new_obj.capacity_for_size["Vast"] = 1;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  break;
                }
              }}
            />
          }
        </Grid>
        { renderCapacityForSizes() }
      </>
    );
  }

  const renderCapacityForSizes = () => {
    const sizes = Object.keys(obj.capacity_for_size);

    return sizes.map((size, key) => {
      let size_options = obj.size === "Vast" ? ["Vast"] : obj.size === "Roomy" ? ["Roomy", "Vast"] : BASTION_FACILITY_SIZES;
      size_options = size_options.filter(o => !sizes.includes(o) || size === o);
      
      return (
        <Grid
          key={size}
          size={{
            xs: 12
          }}>
          <SelectStringToHashBox
            name1="Size"
            name2="Capacity"
            keys={size_options}
            the_key={size}
            value={obj.capacity_for_size[size]}
            onChange={(key: string, value: string | number) => {
              if (typeof value === "number") {
                const new_obj = obj;
                if (size !== key) {
                  delete new_obj.capacity_for_size[size];
                }
                new_obj.capacity_for_size[key] = value;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }
            }}
            onDelete={() => {
              const new_obj = obj;
              delete new_obj.capacity_for_size[size];
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
      );
    });
  }

  const renderLimitations = () => {
    return (
      <>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header padded">
            Limitations
          </span>
          { !disabled &&
            <AddButton
              disabled={disabled}
              onClick={ () => {
                const new_obj = obj;
                new_obj.limitations.push(new CharacterLimitation());
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        { obj.limitations.map(limit => {
          return (
            <Grid
              key={limit.true_id}
              size={{
                xs: 12
              }}>
              <CharacterLimitationBox
                obj={limit}
                disabled={disabled}
                onChange={() => {
                  const new_obj = obj;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
                onDelete={() => {
                  const new_obj = obj;
                  new_obj.limitations = new_obj.limitations.filter(o => o.true_id !== limit.true_id);
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          );
        })}
      </>
    );
  }

  const renderEnlarging = () => {
    if (obj.size === "Cramped") {
      return (
        <>
          <Grid
            className="header"
            size={{
              xs: 12
            }}>
            Enlarging
          </Grid>
          <>
            <Grid
              className="header padded"
              size={{
                xs: 4
              }}>
              Roomy
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <StringBox
                name="Cost (Gold Pieces)"
                value={obj.enlarge_roomy_cost}
                disabled={disabled}
                type="number"
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  obj.enlarge_roomy_cost = +changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <StringBox
                name="Time Required (Days)"
                value={obj.enlarge_roomy_time}
                disabled={disabled}
                type="number"
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  obj.enlarge_roomy_time = +changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </>
          <>
            <Grid
              className="header padded"
              size={{
                xs: 4
              }}>
              Vast
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <StringBox
                name="Cost (Gold Pieces)"
                value={obj.enlarge_vast_cost}
                disabled={disabled}
                type="number"
                onBlur={(changed: string) => {
                  debugger;
                  const new_obj = obj;
                  obj.enlarge_vast_cost = +changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <StringBox
                name="Time Required (Days)"
                value={obj.enlarge_vast_time}
                disabled={disabled}
                type="number"
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  obj.enlarge_vast_time = +changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </>
        </>
      );
    } else if (obj.size === "Roomy") {
      return (
        <>
          <Grid
            className="header"
            size={{
              xs: 12
            }}>
            Enlarging
          </Grid>
          <Grid
            className="header padded"
            size={{
              xs: 4
            }}>
            Vast
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox
              name="Cost (Gold Pieces)"
              value={obj.enlarge_vast_cost}
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                // debugger;
                const new_obj = obj;
                obj.enlarge_vast_cost = +changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox
              name="Time Required (Days)"
              value={obj.enlarge_vast_time}
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                // debugger;
                const new_obj = obj;
                obj.enlarge_vast_time = +changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        </>
      );
    }
    return null;
  }

  const onProfitPercentageAdd = () => {
    const new_obj = obj;
    const levels = Object.keys(new_obj.profit_percent_potence).map(o => +o);
    let level = Math.max(...levels, 0);
    const value = new_obj.profit_percent_potence[level] ? new_obj.profit_percent_potence[level] : 0;
    level++;
    new_obj.profit_percent_potence[level] = value;
    setObj(new_obj);
    setChangeTime(common_util.get_time_number());
  }

  const renderProfitPercentages = () => {
    const levels = Object.keys(obj.profit_percent_potence).map(o => +o);

    return levels.map((level, key) => {
      const level_options = CHARACTER_LEVEL_NUMS.filter(o => !levels.includes(o) || level === o).map(o => `${o}`);
      return (
        <Grid
          key={level}
          container
          spacing={0}
          direction="row"
          className={cx(classes.root, (key % 2 === 1 ? "alternate" : "" ))}
          size={{
            xs: 12
          }}>
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox
              name="Level"
              options={level_options}
              value={`${level}`}
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                const value = new_obj.profit_percent_potence[level];
                delete new_obj.profit_percent_potence[level];
                new_obj.profit_percent_potence[+changed] = value;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <StringBox
              name="Profit Percentage"
              value={obj.profit_percent_potence[level]}
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.profit_percent_potence[level] = +changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        </Grid>
      );
    });
  }

  // const renderEnlargingFor = (for_size: "Roomy" | "Vast") => {
  //   return (
  //     <>
  //       <Grid item xs={4} className="header padded">
  //         { for_size }
  //       </Grid>
  //       <Grid item xs={4}>
  //         <StringBox
  //           name="Cost (Gold Pieces)"
  //           value={obj.enlarging_for_size[for_size].cost}
  //           disabled={disabled}
  //           type="number"
  //           onBlur={(changed: string) => {
  //             debugger;
  //             const new_obj = obj;
  //             obj.enlarging_for_size[for_size].cost = +changed;
  //             setObj(new_obj);
  //             setChangeTime(common_util.get_time_number());
  //           }}
  //         />
  //       </Grid>
  //       <Grid item xs={4}>
  //         <StringBox
  //           name="Time Required (Days)"
  //           value={obj.enlarging_for_size[for_size].time}
  //           disabled={disabled}
  //           type="number"
  //           onBlur={(changed: string) => {
  //             const new_obj = obj;
  //             obj.enlarging_for_size[for_size].time = +changed;
  //             setObj(new_obj);
  //             setChangeTime(common_util.get_time_number());
  //           }}
  //         />
  //       </Grid>
  //     </>
  //   );
  // }
 
  // console.log(obj);
  return (
    <ModelBaseInput 
      obj={obj}
      disabled={disabled}
      change_time={change_time}
      onChange={(changed: ModelBase) => {
        if (changed instanceof BastionFacility) {
          const new_obj = obj;
          new_obj.copy(changed);
          setObj(new_obj);
        }
      }}
      extra_menu_options={ obj.special_facility ? {
        "Abilities": 
          renderAbilities()
        } : undefined
      }>
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 3
          }}>
          <SelectStringBox
            name="Minimum Size"
            value={obj.size}
            options={BASTION_FACILITY_SIZE_OPTIONS}
            disabled={disabled}
            onChange={(changed: BastionFacilitySize) => {
              const new_obj = obj;
              new_obj.size = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <CheckBox
            name="Repeatable"
            value={obj.repeatable}
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.repeatable = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <CheckBox
            name="Special Facility"
            value={obj.special_facility}
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.special_facility = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { obj.special_facility && 
          <>
            <Grid
              size={{
                xs: 3
              }}>
              <StringBox
                name="Hirelings"
                value={`${obj.hirelings}`}
                disabled={disabled}
                type='number'
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  new_obj.hirelings = +changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            { renderSubtypes() }
            { renderCapacityForSize() }
            <Grid
              size={{
                xs: 12
              }}>
              <span className="header padded">
                Profit Percentage For Character Level
              </span>
              { !disabled && 
                <AddButton onClick={onProfitPercentageAdd} />
              }
            </Grid>
            { renderProfitPercentages() }
            { renderLimitations() }
          </>
        }
        { renderEnlarging() }
      </Grid>
    </ModelBaseInput>
  ); 
}